import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';

import {
  DEFAULT_CONTEXT,
  initialPlaybackState,
} from '@/features/AudioPlayer/atoms/core/constants';
import { generateUUID } from '@/utils/uuid';
import {
  audioElementAtom,
  audioPlayerStateAtom,
  playbackAtom,
} from '@/features/AudioPlayer/atoms/core/primaryAtoms';
import { log } from '@/lib/utils';

import type {
  AudioTrack,
  LoadedQueue,
  QueueContext,
  QueueState,
} from '@/features/AudioPlayer/types';

/**
 * QUEUE MANAGEMENT ATOMS
 * These atoms handle operations on the queue such as adding, removing, and navigating
 * through tracks
 */

/**
 * Store the current queue state - derived from audioPlayerStateAtom
 * Provides a convenient accessor for the current queue
 */
export const queueAtom = atom(
  (get) => get(audioPlayerStateAtom).currentQueue,
  (get, set, newQueueState: QueueState) => {
    const currentState = get(audioPlayerStateAtom);

    set(audioPlayerStateAtom, {
      ...currentState,
      currentQueue: newQueueState,
    });
  }
);
queueAtom.debugLabel = 'queueAtom';

/**
 * Access the loaded queues directly
 * These are queues that are ready but not currently active
 */
export const loadedQueuesAtom = atom(
  (get) => get(audioPlayerStateAtom).loadedQueues,
  (get, set, newLoadedQueues: LoadedQueue[]) => {
    const currentState = get(audioPlayerStateAtom);

    set(audioPlayerStateAtom, {
      ...currentState,
      loadedQueues: newLoadedQueues,
    });
  }
);
loadedQueuesAtom.debugLabel = 'loadedQueuesAtom';

/**
 * Find a track in the queue by ID
 * Returns the track and its index if found, or null if not found
 */
export const findTrackInQueueAtom = atom(null, (get, _set, trackId: number) => {
  const queue = get(queueAtom);
  const trackIndex = queue.trackOrder.indexOf(trackId);

  return trackIndex >= 0
    ? {
        track: queue.tracks.get(trackId),
        index: trackIndex,
      }
    : null;
});
findTrackInQueueAtom.debugLabel = 'findTrackInQueueAtom';

/**
 * Find a track in loaded queues
 * This helps determine if a track exists in any pending queues
 * Returns the queue ID and track index if found
 */
export const findTrackInLoadedQueuesAtom = atom(
  (get) =>
    (trackId: number): { queueId: string; trackIndex: number } | null => {
      const { loadedQueues } = get(audioPlayerStateAtom);

      // Find the queue containing the track
      const queueWithTrack = loadedQueues.find((queue) =>
        queue.tracks.has(trackId)
      );

      if (!queueWithTrack) {
        return null;
      }

      // Get the track index from the order array
      const trackIndex = queueWithTrack.trackOrder.indexOf(trackId);

      return {
        queueId: queueWithTrack.id,
        trackIndex,
      };
    }
);
findTrackInLoadedQueuesAtom.debugLabel = 'findTrackInLoadedQueuesAtom';

/**
 * Add tracks to the queue without changing the current track
 * Useful for adding tracks to play next or later
 */
export const addToQueueAtom = atom(null, (get, set, tracks: AudioTrack[]) => {
  const queue = get(queueAtom);
  const newTracks = new Map(queue.tracks);
  const newTrackOrder = [...queue.trackOrder];

  // Add new tracks to both the map and order array
  for (const track of tracks) {
    // For SFX tracks, use track.id (variant ID) as the unique identifier
    // For music tracks, use track.track_id if available, otherwise track.id
    const trackId =
      track.type === 'sfx' ? track.id : (track.track_id ?? track.id);

    if (!newTracks.has(trackId)) {
      newTracks.set(trackId, track);
      newTrackOrder.push(trackId);
    }
  }

  set(queueAtom, {
    ...queue,
    tracks: newTracks,
    trackOrder: newTrackOrder,
  });
});
addToQueueAtom.debugLabel = 'addToQueueAtom';

/**
 * Create a new loaded queue - doesn't affect current playback
 *
 * Key behaviors:
 * - One loaded queue per context (source/sourceId combination)
 * - New queues with same context replace existing ones (when replace=true)
 * - Timestamps track when queues are created for priority determination
 * - Current playback continues uninterrupted until user explicitly plays from loaded queue
 */
export const createLoadedQueueAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      tracks: AudioTrack[];
      context: QueueContext;
      replace?: boolean;
      append?: boolean;
    }
  ) => {
    const { tracks, context, replace = false, append = false } = payload;
    const currentState = get(audioPlayerStateAtom);
    const loadedQueues = [...currentState.loadedQueues];

    // Determine if this queue should replace or update an existing one with the same source
    const sourceKey = `${context.sourceType}:${context.source}:${context.sourceId ?? ''}`;

    const existingQueueIndex = loadedQueues.findIndex(
      (q) =>
        `${q.context.sourceType}:${q.context.source}:${q.context.sourceId ?? ''}` ===
        sourceKey
    );

    // Create the new queue with Maps
    const newTracks = new Map<number, AudioTrack>();
    const newTrackOrder: number[] = [];

    for (const track of tracks) {
      // For SFX tracks, use track.id (variant ID) as the unique identifier
      // For music tracks, use track.track_id if available, otherwise track.id
      const trackId =
        track.type === 'sfx' ? track.id : (track.track_id ?? track.id);

      if (!newTracks.has(trackId)) {
        newTracks.set(trackId, track);
        newTrackOrder.push(trackId);
      }
    }

    const newQueue: LoadedQueue = {
      id: generateUUID(), // Generate a unique ID
      tracks: newTracks,
      trackOrder: newTrackOrder,
      context,
      timestamp: Date.now(),
    };

    if (existingQueueIndex >= 0) {
      if (append) {
        // Append to the existing queue rather than replacing it
        const existingQueue = loadedQueues[existingQueueIndex];
        const combinedTracks = new Map(existingQueue.tracks);
        const combinedOrder = [...existingQueue.trackOrder];

        // Add new tracks, and refresh the data of tracks that already exist.
        // Refreshing matters for synthetic entries (e.g. customize versions)
        // whose `version_preview_uri` is a blob URL that gets regenerated —
        // and the previous one revoked — when the underlying audio changes.
        // Without this, the queue would keep serving a stale/revoked URL.
        for (const track of tracks) {
          // For SFX tracks, use track.id (variant ID) as the unique identifier
          // For music tracks, use track.track_id if available, otherwise track.id
          const trackId =
            track.type === 'sfx' ? track.id : (track.track_id ?? track.id);

          if (!combinedTracks.has(trackId)) {
            combinedOrder.push(trackId);
          }
          combinedTracks.set(trackId, track);
        }

        loadedQueues[existingQueueIndex] = {
          ...existingQueue,
          tracks: combinedTracks,
          trackOrder: combinedOrder,
          timestamp: Date.now(),
        };
      } else if (replace) {
        // Replace the existing queue
        loadedQueues[existingQueueIndex] = newQueue;
      } else {
        // If neither append nor replace, add as a new queue
        loadedQueues.push(newQueue);
      }
    } else {
      // Add as a new queue if no existing queue found
      loadedQueues.push(newQueue);
    }

    // Update the loaded queues
    set(audioPlayerStateAtom, {
      ...currentState,
      loadedQueues,
    });

    const operation =
      existingQueueIndex >= 0
        ? append
          ? 'Appended to'
          : replace
            ? 'Replaced'
            : 'Added'
        : 'Created';

    log(`${operation} loaded queue:`, {
      queueId: newQueue.id,
      tracks: newQueue.tracks.size,
      context: newQueue.context.source,
    });

    return loadedQueues[
      existingQueueIndex >= 0 ? existingQueueIndex : loadedQueues.length - 1
    ].id;
  }
);
createLoadedQueueAtom.debugLabel = 'createLoadedQueueAtom';

/**
 * Push tracks into a queue with improved context awareness
 * Creates a new loaded queue or updates the current queue based on context
 */
export const pushToQueueAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      tracks: AudioTrack[];
      context?: QueueContext;
      replace?: boolean;
      activateQueue?: boolean;
    }
  ) => {
    const {
      tracks,
      context = DEFAULT_CONTEXT,
      replace = false,
      activateQueue = false,
    } = payload;

    const currentState = get(audioPlayerStateAtom);
    const { currentQueue } = currentState;

    // Determine if this matches the current queue context
    const isCurrentQueueContext =
      currentQueue.context.sourceType === context.sourceType &&
      currentQueue.context.source === context.source &&
      currentQueue.context.sourceId === context.sourceId;

    if (isCurrentQueueContext || activateQueue) {
      // This belongs to the current queue or should replace it
      if (replace) {
        // Create new Maps and order array for the tracks
        const newTracks = new Map<number, AudioTrack>();
        const newTrackOrder: number[] = [];

        for (const track of tracks) {
          // For SFX tracks, use track.id (variant ID) as the unique identifier
          // For music tracks, use track.track_id if available, otherwise track.id
          const trackId =
            track.type === 'sfx' ? track.id : (track.track_id ?? track.id);

          if (!newTracks.has(trackId)) {
            newTracks.set(trackId, track);
            newTrackOrder.push(trackId);
          }
        }

        // Replace the entire queue
        set(queueAtom, {
          tracks: newTracks,
          trackOrder: newTrackOrder,
          currentIndex: currentQueue.currentIndex >= 0 ? 0 : -1,
          context,
          history: [],
          timestamp: Date.now(),
        });
      } else {
        // Append to the current queue
        const newTracks = new Map(currentQueue.tracks);
        const newTrackOrder = [...currentQueue.trackOrder];

        for (const track of tracks) {
          // For SFX tracks, use track.id (variant ID) as the unique identifier
          // For music tracks, use track.track_id if available, otherwise track.id
          const trackId =
            track.type === 'sfx' ? track.id : (track.track_id ?? track.id);

          if (!newTracks.has(trackId)) {
            newTracks.set(trackId, track);
            newTrackOrder.push(trackId);
          }
        }

        set(queueAtom, {
          ...currentQueue,
          tracks: newTracks,
          trackOrder: newTrackOrder,
          context: {
            ...currentQueue.context,
            ...context,
          },
          // Keep existing timestamp when appending, don't update it
          timestamp: currentQueue.timestamp,
        });
      }
    } else {
      // Create a new loaded queue instead
      set(createLoadedQueueAtom, { tracks, context, replace });
    }
  }
);
pushToQueueAtom.debugLabel = 'pushToQueueAtom';

/**
 * Remove a track from the queue by index
 * This handles adjusting the current index appropriately
 */
export const removeFromQueueAtom = atom(null, (get, set, index: number) => {
  const queue = get(queueAtom);

  // If the index is invalid, do nothing
  if (index < 0 || index >= queue.trackOrder.length) {
    return;
  }

  const trackId = queue.trackOrder[index];
  const newTracks = new Map(queue.tracks);
  const newTrackOrder = [...queue.trackOrder];

  // Remove the track from both the map and order array
  newTracks.delete(trackId);
  newTrackOrder.splice(index, 1);

  // Adjust currentIndex if needed based on the removed track's position
  let newCurrentIndex = queue.currentIndex;

  if (index === queue.currentIndex) {
    // If removing the current track, don't change the index (it will now point to the next track)
    // But we should also consider if we're removing the last track
    if (newCurrentIndex >= newTrackOrder.length) {
      newCurrentIndex = Math.max(0, newTrackOrder.length - 1);
    }
  } else if (index < queue.currentIndex) {
    // If removing a track before the current track, decrement the index
    newCurrentIndex -= 1;
  }

  set(queueAtom, {
    ...queue,
    tracks: newTracks,
    trackOrder: newTrackOrder,
    currentIndex: newCurrentIndex,
  });
});
removeFromQueueAtom.debugLabel = 'removeFromQueueAtom';

/**
 * Clear the queue entirely
 * Resets both queue and playback state to initial values
 */
export const clearQueueAtom = atom(null, (_get, set) => {
  log('clearQueueAtom called - clearing all queues');

  set(audioPlayerStateAtom, {
    currentQueue: {
      tracks: new Map(),
      trackOrder: [],
      currentIndex: -1,
      context: DEFAULT_CONTEXT,
      history: [],
      timestamp: Date.now(),
    },
    loadedQueues: [],
  });
  set(playbackAtom, initialPlaybackState);
});
clearQueueAtom.debugLabel = 'clearQueueAtom';

/**
 * Activate a loaded queue
 * This replaces the current queue with a loaded queue and starts playback
 * but preserves the previous current queue by moving it to loaded queues
 */
export const activateQueueAtom = atom(
  null,
  (get, set, payload: { queueId: string; startIndex?: number }) => {
    log('activateQueueAtom called with payload:', payload);
    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: 'Activating queue',
      level: 'info',
      data: payload,
    });

    const { queueId } = payload;
    const currentState = get(audioPlayerStateAtom);
    const { currentQueue, loadedQueues } = currentState;

    // Find the queue to activate
    const queueToActivate = loadedQueues.find((q) => q.id === queueId);

    if (!queueToActivate) {
      log('Queue not found:', queueId);

      return;
    }

    // Create a new current queue from the loaded queue
    const newCurrentQueue: QueueState = {
      tracks: new Map(queueToActivate.tracks),
      trackOrder: [...queueToActivate.trackOrder],
      currentIndex: payload.startIndex ?? 0,
      context: queueToActivate.context,
      history: [],
      timestamp: Date.now(), // Set fresh activation timestamp
    };

    // Keep the activated queue in loaded queues so it remains available
    // even if another queue replaces the current queue
    const newLoadedQueues = [...loadedQueues];

    // If we have a current queue and it's different from the one we're activating
    if (currentQueue.trackOrder.length > 0) {
      const currentTrackIds = new Set(currentQueue.trackOrder);
      const loadedTrackIds = new Set(queueToActivate.trackOrder);

      // Only save the current queue if it's different from the one we're activating
      if (currentTrackIds !== loadedTrackIds) {
        // Check if there's already a similar loaded queue (same context)
        const contextKey = `${currentQueue.context.sourceType}:${
          currentQueue.context.source
        }:${currentQueue.context.sourceId ?? ''}`;

        const existingSimilarQueueIndex = newLoadedQueues.findIndex(
          (q) =>
            `${q.context.sourceType}:${q.context.source}:${q.context.sourceId ?? ''}` ===
            contextKey
        );

        if (existingSimilarQueueIndex >= 0) {
          // Replace the existing similar queue
          newLoadedQueues[existingSimilarQueueIndex] = {
            id: generateUUID(), // Generate a new unique ID
            tracks: new Map(currentQueue.tracks),
            trackOrder: [...currentQueue.trackOrder],
            context: currentQueue.context,
            timestamp: currentQueue.timestamp,
          };
        } else {
          // Create a LoadedQueue from the current queue
          const savedQueue: LoadedQueue = {
            id: generateUUID(), // Generate a unique ID
            tracks: new Map(currentQueue.tracks),
            trackOrder: [...currentQueue.trackOrder],
            context: currentQueue.context,
            timestamp: currentQueue.timestamp,
          };

          // Add the previous current queue to loaded queues
          newLoadedQueues.push(savedQueue);
        }
      }
    }

    // Update the state
    set(audioPlayerStateAtom, {
      currentQueue: newCurrentQueue,
      loadedQueues: newLoadedQueues,
    });

    log('Activated queue:', {
      queueId,
      tracks: newCurrentQueue.tracks.size,
      context: newCurrentQueue.context.source,
    });

    // Try to load the audio for the new current track
    const currentTrackId =
      newCurrentQueue.trackOrder[newCurrentQueue.currentIndex];

    const currentTrack = currentTrackId
      ? newCurrentQueue.tracks.get(currentTrackId)
      : null;

    const audioElement = get(audioElementAtom);

    if (currentTrack && audioElement && currentTrack.version_preview_uri) {
      audioElement.src = currentTrack.version_preview_uri;
      Sentry.addBreadcrumb({
        category: 'audio_player',
        message: 'Set audio source',
        level: 'info',
        data: {
          trackId: currentTrack.id,
          trackName: currentTrack.name,
          previewUri: `${currentTrack.version_preview_uri.slice(0, 50)}...`,
        },
      });
    }
  }
);
activateQueueAtom.debugLabel = 'activateQueueAtom';

/**
 * Set current queue from a page queue atom
 * This is a simpler alternative to activateQueueAtom for search pages
 * that already have the queue ready in atom form
 */
export const setCurrentQueueFromPageAtom = atom(
  null,
  (get, set, pageQueueState: QueueState) => {
    // Set the page queue as the current queue, preserving the currentIndex
    const targetIndex =
      pageQueueState.currentIndex >= 0 ? pageQueueState.currentIndex : 0;

    log('setCurrentQueueFromPageAtom called', {
      tracks: pageQueueState.tracks.size,
      context: pageQueueState.context.source,
      requestedIndex: pageQueueState.currentIndex,
      targetIndex,
    });

    // Get current state to preserve loadedQueues
    const currentState = get(audioPlayerStateAtom);

    set(audioPlayerStateAtom, {
      ...currentState,
      currentQueue: {
        ...pageQueueState,
        currentIndex: targetIndex,
        timestamp: Date.now(), // Fresh timestamp
      },
    });

    // Try to load the audio for the target track (not always the first)
    const targetTrackId = pageQueueState.trackOrder[targetIndex];
    const targetTrack = targetTrackId
      ? pageQueueState.tracks.get(targetTrackId)
      : null;
    const audioElement = get(audioElementAtom);

    // Skip when the source is already loaded: reassigning src (even to the
    // same URL) reruns the media load algorithm and resets the playhead,
    // which restarted paused tracks on resume (DEV-6234)
    if (
      targetTrack &&
      audioElement &&
      targetTrack.version_preview_uri &&
      !audioElement.src.includes(targetTrack.version_preview_uri)
    ) {
      audioElement.src = targetTrack.version_preview_uri;
      Sentry.addBreadcrumb({
        category: 'audio_player',
        message: 'Set audio source from page queue',
        level: 'info',
        data: {
          trackId: targetTrack.id,
          trackName: targetTrack.name,
          previewUri: `${targetTrack.version_preview_uri.slice(0, 50)}...`,
        },
      });
    }
  }
);
setCurrentQueueFromPageAtom.debugLabel = 'setCurrentQueueFromPageAtom';
